import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 استدعاء الترجمة
import { 
  Plus, Search, ChevronLeft, ChevronRight, ArrowUpDown, 
  Activity, FileText, Calendar, Filter, X, Globe, Building2, RefreshCw
} from 'lucide-react';
import toast from 'react-hot-toast';

// استيراد السياقات الموحدة
import { usePermissions } from './usePermissions';
import { useFontSize } from './FontSizeContext';

const PMList = () => {
  const [pms, setPms] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // 🎯 إعدادات الترجمة والاتجاه
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

  // جلب معلومات الجلسة والصلاحيات
  const session = JSON.parse(localStorage.getItem('user') || '{}');
  const sessionSite = session.site_id || '';
  const sessionOrg = session.org_id || '';
  const { canCreate } = usePermissions('pm');
  const isSuperAdmin = session.role?.name === 'SUPER_ADMIN';
  
  // سياق حجم الخط
  const fontContext = useFontSize();
  const fontSize = fontContext?.fontSize || 13;

  // --- States الفلاتر والبحث ---
  const [filters, setFilters] = useState({
    pmnum: "", description: "", status: "", assetnum: "", location_id: "", worktype: "", site_id: "", org_id: ""
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrg, setSelectedOrg] = useState("");
  const [selectedSite, setSelectedSite] = useState("");
  const [orgsList, setOrgsList] = useState([]);
  const [sitesList, setSitesList] = useState([]);
  const [showAllSites, setShowAllSites] = useState(false);

  // --- إعدادات الترتيب والباجينج السيرفر ---
  const [sortConfig, setSortConfig] = useState({ key: 'pmnum', direction: 'desc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  // 🎯 دالة جلب البيانات من السيرفر
  const fetchPMs = useCallback(async (isAll = showAllSites) => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}` };
    
    try {
      const forceAll = isSuperAdmin && (selectedOrg !== "" || selectedSite !== "");
      const effectiveAll = isAll || forceAll;

      const params = {
        page: currentPage,
        limit: itemsPerPage,
        all_sites: Boolean(effectiveAll),
        q: searchTerm,
        pmnum: filters.pmnum,
        description: filters.description,
        status: filters.status,
        assetnum: filters.assetnum,
        location_id: filters.location_id,
        worktype: filters.worktype,
        org_id: effectiveAll ? (selectedOrg || filters.org_id) : sessionOrg,
        site_id: effectiveAll ? (selectedSite || filters.site_id) : sessionSite,
        sortBy: sortConfig.key,
        sortDir: sortConfig.direction,
        user_siteid: sessionSite,
        user_orgid: sessionOrg
      };

      const res = await axios.get('/api/pm', { headers, params });
      
      setPms(res.data.data || []);
      setTotalItems(res.data.total || 0);
    } catch (err) {
      console.error("Fetch Error:", err);
      toast.error(t('pm.messages.fetchListError'));
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchTerm, filters, selectedOrg, selectedSite, showAllSites, sortConfig, isSuperAdmin, sessionSite, sessionOrg, t]);

  useEffect(() => { fetchPMs(); }, [fetchPMs]);

  useEffect(() => {
    if (isSuperAdmin) {
      const token = localStorage.getItem('token');
      axios.get('/api/organizations', { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => setOrgsList(res.data)).catch(err => console.error(err));
    }
  }, [isSuperAdmin]);

  useEffect(() => {
    if (selectedOrg) {
      const org = orgsList.find(o => o.org_id === selectedOrg);
      setSitesList(org ? org.sites : []);
    } else { setSitesList([]); }
    setSelectedSite("");
    setCurrentPage(1);
  }, [selectedOrg, orgsList]);

  const handleRowClick = (pm_id) => {
    const activeParams = {
        ...filters, searchTerm, page: currentPage, sortBy: sortConfig.key, sortDir: sortConfig.direction,
        all_sites: showAllSites, user_siteid: selectedSite || sessionSite, orgid: selectedOrg || sessionOrg
    };
    const queryString = new URLSearchParams(Object.fromEntries(Object.entries(activeParams).filter(([_, v]) => v !== ''))).toString();
    navigate(`/pm/${pm_id}${queryString ? '?' + queryString : ''}`);
  };

  const requestSort = (key) => {
    setSortConfig(prev => ({ key, direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc' }));
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  useEffect(() => {
    const openAll = () => { if(isSuperAdmin) { setShowAllSites(true); setCurrentPage(1); } };
    const viewMine = () => { setShowAllSites(false); setCurrentPage(1); };
    window.addEventListener('GLOBAL_VIEW_ALL_SITES', openAll);
    window.addEventListener('GLOBAL_VIEW_MY_SITE', viewMine);
    return () => {
      window.removeEventListener('GLOBAL_VIEW_ALL_SITES', openAll);
      window.removeEventListener('GLOBAL_VIEW_MY_SITE', viewMine);
    };
  }, [isSuperAdmin]);

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  return (
    <div 
      className={`flex flex-col h-full bg-[#f3f4f6] font-bold uppercase tracking-tighter overflow-hidden ${isRTL ? 'font-cairo text-right dir-rtl' : 'font-sans text-left'}`}
      style={{ fontSize: `${fontSize}px` }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      
      {/* 1. Header Panel & Global Search */}
      <div className="bg-white p-3 border-b shadow-sm z-10 space-y-3">
        <div className={`flex justify-between items-center `}>
          <div className={`flex items-center gap-6 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <h2 className={`text-[#005495] font-black italic text-xl flex items-center gap-2 uppercase tracking-tighter `}>
              <RefreshCw size={22} className={`${loading ? 'animate-spin text-blue-500' : 'text-blue-900 cursor-pointer'}`} onClick={() => fetchPMs()} /> 
              {t('pm.headers.masterDir')}
            </h2>
            
            <div className="relative group">
              <Search className={`absolute top-2.5 text-gray-400 group-focus-within:text-blue-500 ${isRTL ? 'right-3' : 'left-3'}`} size={16} />
              <input 
                className={`py-2 border border-gray-300 w-[450px] outline-none focus:border-blue-600 shadow-inner rounded-sm font-black text-blue-900 placeholder:font-normal placeholder:italic uppercase ${isRTL ? 'pr-10 pl-4 text-right' : 'pl-10 pr-4 text-left'}`} 
                placeholder={t('pm.placeholders.globalSearch')} 
                value={searchTerm}
                onChange={(e) => {setSearchTerm(e.target.value); setCurrentPage(1);}}
              />
            </div>
          </div>

          {canCreate && (
            <button onClick={() => navigate('/pm/new')} className={`bg-[#005495] text-white px-8 py-2.5 rounded-sm font-black flex items-center gap-2 hover:bg-black shadow-lg transition-all active:scale-95 ${isRTL ? 'ml-4' : 'mr-4'}`}>
              <Plus size={18} strokeWidth={3} /> {t('pm.actions.newPM')}
            </button>
          )}
        </div>

        {/* 2. Advanced Organization/Site Filters (For Admins) */}
        {isSuperAdmin && (
          <div className={`flex items-center gap-4 bg-gray-50 p-2.5 rounded-sm border border-gray-200 w-fit shadow-sm animate-in slide-in-from-left duration-300 ${isRTL ? 'mr-4' : 'ml-4'}`}>
            <div className={`flex items-center gap-2 border-gray-300 ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
              <Globe size={16} className="text-blue-600" />
              <span className="text-gray-400 italic text-[10px]">{t('assets.list.all_orgs')}</span>
              <select 
                className={`bg-transparent outline-none font-black text-blue-900 uppercase cursor-pointer ${isRTL ? 'dir-rtl' : ''}`}
                value={selectedOrg}
                onChange={(e) => setSelectedOrg(e.target.value)}
              >
                <option value="">{t('assets.list.all_orgs')}</option>
                {orgsList.map(org => (<option key={org.org_id} value={org.org_id}>{org.org_id}</option>))}
              </select>
            </div>

            <div className={`flex items-center gap-2 border-gray-300 ${isRTL ? 'border-l pl-6' : 'border-r pr-6'}`}>
              <Building2 size={16} className="text-blue-600" />
              <span className="text-gray-400 italic text-[10px]">{t('assets.list.all_sites')}</span>
              <select 
                className={`bg-transparent outline-none font-black text-blue-900 uppercase cursor-pointer disabled:opacity-30 ${isRTL ? 'dir-rtl' : ''}`}
                value={selectedSite}
                onChange={(e) => setSelectedSite(e.target.value)}
                disabled={!selectedOrg}
              >
                <option value="">{t('assets.list.all_sites')}</option>
                {sitesList.map(site => (<option key={site.site_id} value={site.site_id}>{site.site_id}</option>))}
              </select>
            </div>

            {(selectedOrg || searchTerm || filters.pmnum) && (
              <button 
                onClick={() => {
                    setSelectedOrg(""); setSelectedSite(""); setSearchTerm(""); 
                    setFilters({pmnum: "", description: "", status: "", assetnum: "", location_id: "", worktype: "", site_id: "", org_id: ""});
                    setCurrentPage(1);
                }}
                className={`text-[10px] text-red-600 font-black uppercase hover:bg-red-50 px-3 py-1 rounded transition-all flex items-center gap-1 border border-red-100 shadow-sm `}
              >
                <X size={12}/> {t('common.clearAll')}
              </button>
            )}
          </div>
        )}
      </div>

      {/* 3. Table Section */}
      <div className="flex-1 overflow-auto p-4 custom-scrollbar bg-[#f8f9fb]">
        <div className="bg-white border-2 border-gray-200 shadow-xl rounded-sm overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead className="bg-[#1a3353] text-white font-black uppercase sticky top-0 z-30 shadow-md">
              <tr className={isRTL ? 'text-right' : 'text-left'}>
                {[
                  { label: t('pm.fields.pmNum'), key: "pmnum", width: "w-48" },
                  { label: t('pm.fields.description'), key: "description", width: "" },
                  { label: t('pm.fields.status'), key: "status", width: "w-32" },
                  { label: t('assets.headers.asset_num'), key: "assetnum", width: "w-40" },
                  { label: t('wo.fields.workType'), key: "worktype", width: "w-32" },
                  { label: t('pm.fields.location'), key: "location_id", width: "w-40" },
                  { label: t('pm.fields.nextDue'), key: "nextdate", width: "w-40" }
                ].map(col => (
                  <th 
                    key={col.key} 
                    className={`p-4 border-x border-white/10 cursor-pointer hover:bg-blue-800 transition-colors ${col.width}`}
                    onClick={() => requestSort(col.key)}
                  >
                    <div className={`flex items-center justify-between gap-2 `}>
                      {col.label}
                      <ArrowUpDown size={12} className={sortConfig.key === col.key ? "text-orange-400" : "text-white/30"} />
                    </div>
                  </th>
                ))}
              </tr>
              {/* Column Search Row */}
              <tr className={`bg-slate-100 shadow-inner `}>
                <td className="p-1.5 border-x border-gray-300"><input name="pmnum" value={filters.pmnum} onChange={handleFilterChange} className={`w-full border border-gray-300 p-1.5 outline-none font-black text-blue-900 uppercase ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} /></td>
                <td className="p-1.5 border-x border-gray-300"><input name="description" value={filters.description} onChange={handleFilterChange} className={`w-full border border-gray-300 p-1.5 outline-none font-bold ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} /></td>
                <td className="p-1.5 border-x border-gray-300">
                  <select name="status" value={filters.status} onChange={handleFilterChange} className={`w-full border border-gray-300 p-1.5 outline-none font-black text-blue-900 uppercase ${isRTL ? 'text-right dir-rtl' : ''}`}>
                    <option value="">{t('locationList.optAll')}</option>
                    <option value="ACTIVE">ACTIVE</option>
                    <option value="DRAFT">DRAFT</option>
                    <option value="INACTIVE">INACTIVE</option>
                  </select>
                </td>
                <td className="p-1.5 border-x border-gray-300"><input name="assetnum" value={filters.assetnum} onChange={handleFilterChange} className={`w-full border border-gray-300 p-1.5 outline-none font-black uppercase ${isRTL ? 'text-right' : ''}`} placeholder={t('common.filter')} /></td>
                <td className="p-1.5 border-x border-gray-300">
                  <select name="worktype" value={filters.worktype} onChange={handleFilterChange} className={`w-full border border-gray-300 p-1.5 outline-none font-black text-blue-900 uppercase ${isRTL ? 'text-right dir-rtl' : ''}`}>
                    <option value="">{t('locationList.optAll')}</option>
                    <option value="PM">PM</option>
                    <option value="EM">EM</option>
                  </select>
                </td>
                <td className="p-1.5 border-x border-gray-300"><input name="location_id" value={filters.location_id} onChange={handleFilterChange} className={`w-full border border-gray-300 p-1.5 outline-none font-black uppercase text-center`} placeholder={t('common.filter')} /></td>
                <td className="p-1.5"></td>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr><td colSpan="7" className="p-32 text-center italic text-blue-900 font-black animate-pulse text-sm uppercase tracking-widest bg-gray-50">{t('pm.messages.syncingList')}</td></tr>
              ) : pms.length === 0 ? (
                <tr><td colSpan="7" className="p-32 text-center italic text-gray-400 text-xs font-black uppercase bg-gray-50 border-dashed border-4 m-4">{t('pm.messages.noRecords')}</td></tr>
              ) : pms.map((pm, i) => (
                <tr key={i} className={`hover:bg-blue-50 cursor-pointer group transition-all ${isRTL ? 'text-right' : 'text-left'}`} onClick={() => handleRowClick(pm.pmnum)}>
                  <td className="p-4 border-x border-gray-100 font-black text-blue-900 uppercase group-hover:text-blue-600 transition-colors text-[0.9]">{pm.pmnum}</td>
                  <td className="p-4 border-x border-gray-100 text-gray-700 font-bold">{pm.description}</td>
                  <td className="p-4 border-x border-gray-100 text-center">
                    <span className={`px-4 py-1 rounded-sm text-[9px] font-black shadow-sm ring-1 ring-inset ${pm.status === 'ACTIVE' ? 'bg-green-100 text-green-700 ring-green-600/20' : 'bg-gray-100 text-gray-600 ring-gray-500/20'}`}>
                      {pm.status}
                    </span>
                  </td>
                  <td className="p-4 border-x border-gray-100 font-black text-blue-800 text-center uppercase">{pm.assetnum || '---'}</td>
                  <td className="p-4 border-x border-gray-100 text-center font-black text-gray-500">{pm.worktype}</td>
                  <td className="p-4 border-x border-gray-100 text-center font-black text-gray-700 italic">{pm.location_id || '---'}</td>
                  <td className="p-4 text-center font-black text-red-600 italic tracking-tighter bg-red-50/10">
                    <div className={`flex items-center justify-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                       <Calendar size={14} />
                       {pm.nextdate?.split('T')[0] || '---'}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* 4. Pagination Footer */}
      <div className={`bg-[#1a3353] text-white p-3 px-10 flex justify-between items-center shadow-2xl z-40 border-t border-white/10 `}>
        <div className={`flex items-center gap-10 text-[10px] font-black uppercase tracking-widest `}>
          <div className={`flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <span className="text-blue-400 italic">{t('pm.footer.dbTotal')}</span>
            <span className="bg-blue-900 px-4 py-1.5 rounded-sm shadow-inner border border-white/10 text-orange-400 text-sm">{totalItems}</span>
          </div>
          <div className={`flex items-center gap-2 border-white/10 ${isRTL ? 'border-r pr-10 flex-row-reverse' : 'border-l pl-10'}`}>
            <span className="text-blue-400 italic">{t('pm.footer.currentPage')}</span>
            <span className="text-white text-sm">{currentPage}</span>
            <span className="text-white/30 mx-2">/</span>
            <span className="text-white text-sm">{totalPages || 1}</span>
          </div>
        </div>
        
        <div className={`flex items-center gap-3 `}>
          <span className="text-[9px] text-gray-400 italic mx-4">{t('pm.footer.useNavArrows')}</span>
          <button 
            disabled={currentPage === 1} 
            onClick={() => setCurrentPage(p => p - 1)} 
            className="p-2.5 border border-white/20 rounded-sm disabled:opacity-10 hover:bg-orange-500 hover:border-orange-500 transition-all shadow-md active:scale-90 bg-white/5"
          >
            <ChevronLeft size={22} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
          <button 
            disabled={currentPage >= totalPages} 
            onClick={() => setCurrentPage(p => p + 1)} 
            className="p-2.5 border border-white/20 rounded-sm disabled:opacity-10 hover:bg-orange-500 hover:border-orange-500 transition-all shadow-md active:scale-90 bg-white/5"
          >
            <ChevronRight size={22} strokeWidth={3} className={isRTL ? 'rotate-180' : ''}/>
          </button>
        </div>
      </div>
    </div>
  );
};

export default PMList;